import CompleteUserProfileModal from '@/components/CompleteUserProfileModal'
import ErrorMessage from '@/components/ErrorMessage'
import Loading from '@/components/Loading'
import LoginSignup from '@/components/LoginSignup'
import InviteAuthPrompt from '@/components/InviteAuthPrompt'
import { Authenticated, Unauthenticated } from '@/utils/auth/auth'
import { getCurrentUser } from '@/utils/auth/UserProvider'
import { useInvitations, useInvitationDetails } from '@/utils/hooks/invitations'
import { useCurrentUserOrgs } from '@/utils/hooks/orgs'
import {
  setPostLoginRedirect,
  clearPostLoginRedirect,
} from '@/utils/postLoginRedirect'
import {
  Box,
  Alert,
  Card,
  CardContent,
  Typography,
  Button,
  Collapse,
} from '@mui/material'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { useState, useEffect, useRef } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import { resendInvitationVerification } from '@/api/orgUsersAPI'
import { useFocusManagement } from '@/utils/hooks/useFocusManagement'
import { usePageTitle } from '@/utils/hooks/usePageTitle'

export default function InvitationPage() {
  usePageTitle('Invitation')

  const { invitationToken } = useParams()
  const [searchParams] = useSearchParams()
  // Email-invite links carry ?action=login|signup to bias the pre-login page.
  // Direct-link invites (and older emails) have no param and fall back to the
  // equal-weight login/signup page.
  const inviteAction = searchParams.get('action')
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  // The unverified-email path: show a "verify then continue" step instead of a
  // plain error. One verification email is sent automatically on entering it;
  // further sends are user-initiated via the Resend button.
  const [verificationNeeded, setVerificationNeeded] = useState(false)
  // Which action the user attempted when they hit the verify step, so
  // "Continue" re-runs the same one (accepting vs declining).
  const [verifyingForStatus, setVerifyingForStatus] = useState<
    'accepted' | 'declined'
  >('accepted')
  const [resending, setResending] = useState(false)
  const [resendMessage, setResendMessage] = useState<string | null>(null)
  // Toggles the "clicked the link but saw an error?" explainer that covers the
  // Outlook/Safe Links case (the link scanner consumes the link, so the user
  // hits an Auth0 error page even though they're now verified).
  const [showVerifyHelp, setShowVerifyHelp] = useState(false)
  const initialVerificationSent = useRef(false)
  const focusRef = useFocusManagement()
  const user = getCurrentUser()
  const token = user?.token as string
  const { updateInvitation } = useInvitations(token, '')
  const { data: invitationDetails, isLoading } = useInvitationDetails(
    token,
    invitationToken || '',
  )

  const { refetch } = useCurrentUserOrgs(token, user.user, [])

  useEffect(() => {
    setLoading(isLoading)
  }, [isLoading])

  // When the user returns to this tab (e.g. after verifying their email in
  // another tab), clear any error so the Accept/Decline buttons re-enable and
  // they can try again without reloading.
  useEffect(() => {
    function onVisible() {
      if (document.visibilityState === 'visible') {
        setError(null)
        // Drop any stale "still isn't verified" note so a returning user
        // (who may have just verified in the other tab) gets a clean slate.
        setResendMessage(null)
      }
    }
    document.addEventListener('visibilitychange', onVisible)
    return () => document.removeEventListener('visibilitychange', onVisible)
  }, [])

  async function handleResponse(status: 'accepted' | 'declined') {
    try {
      if (!invitationToken) {
        setError('Invalid invitation link')
        return
      }

      if (!token || !user.user) {
        setError('You are not logged in')
        return
      }

      await updateInvitation.mutateAsync({
        invitationUuid: invitationToken,
        status,
        token,
      })

      // Clear the post-login redirect now that the invitation has been handled
      clearPostLoginRedirect()

      if (status === 'accepted') {
        await refetch()
        window.location.href = '/' // forces a refresh for auth provider
      } else {
        window.location.href = '/'
      }
    } catch (err) {
      // Clear the verify step by default; only the verification branch below
      // re-sets it. This stops a later failure for a different reason (expired,
      // already a member, mismatch, network) leaving the verify panel stuck on
      // screen alongside a contradictory error.
      setVerificationNeeded(false)
      if (err instanceof Error && err.message.includes('email')) {
        setError(
          'You are logged in with an email that does not match the invitation.',
        )
      } else if (err instanceof Error && err.message.includes('not found')) {
        setError(
          'Invitation not found. It may have been deleted by the inviting user.',
        )
      } else if (err instanceof Error && err.message.includes('Verification')) {
        setVerificationNeeded(true)
        setVerifyingForStatus(status)
        // Set redirect so user returns here after email verification
        setPostLoginRedirect(window.location.pathname)
        // Send one verification email automatically the first time we land on
        // this step, so the user has a fresh link without hunting for the
        // signup one or clicking anything. "Continue" re-checks re-enter this
        // branch, but the ref guard stops it re-sending on every retry.
        if (!initialVerificationSent.current) {
          initialVerificationSent.current = true
          resendVerification({ manual: false })
        } else {
          // A "Continue" re-check that still came back unverified.
          setResendMessage(
            "Your email still isn't verified. Please click the link in the " +
              'email we sent, then click Continue again.',
          )
        }
      } else if (err instanceof Error && err.message.includes('already part')) {
        setError('You are already a member of this organisation.')
      } else if (err instanceof Error && err.message.includes('expired')) {
        setError('Invitation has expired. Please request a new one.')
      } else {
        setError('Sorry - Failed to process invitation. Please try again.')
      }
    }
  }

  async function resendVerification({ manual }: { manual: boolean }) {
    if (!token) return
    try {
      setResending(true)
      setResendMessage(null)
      const { sent } = await resendInvitationVerification(token)
      if (manual) {
        setResendMessage(
          sent
            ? 'Verification email sent. Check your inbox and your spam/junk folder.'
            : 'Good news - your email is already verified! Click Continue to proceed.',
        )
      }
    } catch {
      setResendMessage(
        'We could not resend the email just now. Please try again shortly.',
      )
    } finally {
      setResending(false)
    }
  }

  if (isLoading || loading) {
    return <Loading />
  }

  return (
    <>
      <Unauthenticated>
        {inviteAction === 'login' || inviteAction === 'signup' ? (
          <InviteAuthPrompt action={inviteAction} />
        ) : (
          <LoginSignup />
        )}
      </Unauthenticated>
      <Authenticated>
        <>
          {!user || (!token && <ErrorMessage />)}

          {user?.user && (!user.user.first_name || !user.user.last_name) && (
            <CompleteUserProfileModal
              isOpen
              onComplete={() => {}}
              token={token}
              userData={user.user}
            />
          )}

          {!invitationDetails && !isLoading ? (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '40vh',
              }}
            >
              <Box sx={{ maxWidth: 600, mx: 'auto', mt: 4, p: 2 }}>
                <Alert severity="error">
                  We were unable to find this invitation, it may have been
                  deleted by the inviting user
                </Alert>
              </Box>
            </Box>
          ) : (
            <Box
              sx={{
                maxWidth: 750,
                mx: 'auto',
                mt: 4,
                p: 2,
                // Extra breathing room at the bottom on mobile so the card
                // isn't flush against the viewport edge / nav.
                pb: { xs: 18, sm: 2 },
                textAlign: 'center',
              }}
            >
              <Card>
                <CardContent>
                  <Typography
                    variant="h5"
                    component="h1"
                    ref={focusRef}
                    tabIndex={-1}
                    gutterBottom
                    sx={{
                      mb: 2,
                      '&:focus': {
                        outline: 'none',
                      },
                    }}
                  >
                    Organisation Invitation
                  </Typography>

                  {error && (
                    <Alert
                      severity="error"
                      sx={{ mb: 2, whiteSpace: 'pre-wrap' }}
                    >
                      {error}
                    </Alert>
                  )}

                  <Typography>
                    You have been invited to join{' '}
                    <strong>{invitationDetails.org_name}</strong>
                  </Typography>
                  {user && user.user && (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        my: 2,
                        whiteSpace: 'pre-wrap',
                        width: '80%',
                        mx: 'auto',
                      }}
                    >
                      You're currently logged in with the email -{' '}
                      {user.user.email}
                      {invitationDetails && invitationDetails.invitee_email && (
                        <span style={{ fontStyle: 'italic' }}>
                          <br /> <br />
                          Please ensure you are logged into the account that
                          matches the email address this invitation was sent to
                          before responding.{' '}
                        </span>
                      )}
                      <br />
                      <br />
                      <span style={{ fontStyle: 'italic' }}>
                        To accept this invitation with a different account,
                        please log out and sign in with your preferred account.
                      </span>
                    </Typography>
                  )}
                  {verificationNeeded ? (
                    <Box sx={{ mt: 3 }}>
                      <Alert
                        severity="warning"
                        role="status"
                        aria-live="polite"
                        sx={{ mb: 2, textAlign: 'left' }}
                      >
                        <Typography sx={{ fontWeight: 600, mb: 1 }}>
                          Please verify your email address
                        </Typography>
                        We've sent a verification email to{' '}
                        <span style={{ fontWeight: 600 }}>
                          {user?.user?.email}
                        </span>
                        . Open it and click the verification link, then come
                        back here and click{' '}
                        <span style={{ fontWeight: 600 }}>Continue</span> below.
                        <br />
                        <br />
                        Can't find it? Check your spam/junk folder, or use{' '}
                        <span style={{ fontWeight: 600 }}>Resend email</span>.
                        <br />
                        Still not receiving it? Please contact us at{' '}
                        <a
                          href="mailto:help@fundsorter.com"
                          style={{ textDecoration: 'none', color: 'inherit' }}
                        >
                          help@fundsorter.com
                        </a>
                        .
                        <Box sx={{ mt: 1.5 }}>
                          <Button
                            variant="text"
                            disableRipple
                            startIcon={<HelpOutlineIcon fontSize="small" />}
                            endIcon={
                              <ExpandMoreIcon
                                fontSize="small"
                                sx={{
                                  transition: 'transform 0.2s',
                                  transform: showVerifyHelp
                                    ? 'rotate(180deg)'
                                    : 'none',
                                }}
                              />
                            }
                            onClick={() => setShowVerifyHelp((v) => !v)}
                            aria-expanded={showVerifyHelp}
                            sx={{
                              textTransform: 'none',
                              color: '#1C3045',
                              pt: 2,
                              pb: 0.25,
                              px: 0.25,
                              minWidth: 0,
                              fontWeight: 600,
                              textDecoration: 'underline',
                              '& .MuiButton-startIcon': { ml: 0, mr: 0.5 },
                              '&:hover': {
                                backgroundColor: 'transparent',
                                textDecoration: 'underline',
                              },
                            }}
                          >
                            Clicked the verification link but saw an error?
                          </Button>
                          <Collapse in={showVerifyHelp} unmountOnExit>
                            <Typography variant="body2" sx={{ mt: 1 }}>
                              Some email providers (Microsoft Outlook in
                              particular) automatically open links to scan them
                              for security. That scan can use up the one-time
                              verification link before you get to it, so you may
                              land on an error page such as "this site can't be
                              reached". If that happened,{' '}
                              <span style={{ fontWeight: 600 }}>
                                your email address has already been verified
                              </span>
                              . Just click{' '}
                              <span style={{ fontWeight: 600 }}>
                                I've verified — Continue
                              </span>{' '}
                              below to finish accepting your invitation.
                            </Typography>
                          </Collapse>
                        </Box>
                      </Alert>
                      {resendMessage && (
                        <Alert
                          severity="info"
                          role="status"
                          aria-live="polite"
                          sx={{ mb: 2, textAlign: 'left' }}
                        >
                          {resendMessage}
                        </Alert>
                      )}
                      <Box
                        sx={{
                          display: 'flex',
                          gap: 2,
                          justifyContent: 'center',
                          mt: 2,
                        }}
                      >
                        <Button
                          variant="outlined"
                          disableRipple
                          disabled={resending}
                          onClick={() => resendVerification({ manual: true })}
                          sx={{
                            borderColor: '#1C3045',
                            color: '#1C3045',
                            textTransform: 'none',
                          }}
                        >
                          {resending ? 'Sending…' : 'Resend email'}
                        </Button>
                        <Button
                          variant="contained"
                          disableRipple
                          onClick={() => handleResponse(verifyingForStatus)}
                          sx={{
                            bgcolor: '#1C3045',
                            color: 'white',
                            textTransform: 'none',
                          }}
                        >
                          I've verified — Continue
                        </Button>
                      </Box>
                    </Box>
                  ) : invitationDetails &&
                    invitationDetails.status === 'pending' ? (
                    <Box
                      sx={{
                        display: 'flex',
                        gap: 2,
                        justifyContent: 'flex-end',
                        mt: 4,
                      }}
                    >
                      {invitationDetails.invitee_email && (
                        <Button
                          variant="outlined"
                          disableRipple
                          disabled={error ? true : false}
                          onClick={() => handleResponse('declined')}
                          sx={{
                            borderColor: '#1C3045',
                            color: '#1C3045',
                            textTransform: 'none',
                            '&:focus': {
                              backgroundColor: '#e8e8e8',
                            },
                          }}
                        >
                          Decline
                        </Button>
                      )}
                      <Button
                        variant="contained"
                        disableRipple
                        onClick={() => handleResponse('accepted')}
                        disabled={error ? true : false}
                        sx={{
                          bgcolor: '#1C3045',
                          color: 'white',
                          textTransform: 'none',
                        }}
                      >
                        Accept Invitation
                      </Button>
                    </Box>
                  ) : invitationDetails.status === 'declined' ? (
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 2,
                        mt: 3,
                      }}
                    >
                      <Alert severity="info" sx={{ textAlign: 'left' }}>
                        You have declined this invitation. <br />
                        If this was a mistake, please contact the organisation
                        to send you a new invitation.
                      </Alert>
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        display: 'flex',
                        gap: 2,
                        justifyContent: 'center',
                        mt: 3,
                      }}
                    >
                      <Alert severity="success">
                        You have already accepted this invitation
                      </Alert>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Box>
          )}
        </>
      </Authenticated>
    </>
  )
}
